// Central hotkey registry and helpers
const L = console.log

/**
 * Target "category"
 * This is a grouping construct 
 */
export interface ITargetCat {
  desc: string
}

// KEY MODIFIERS: M = meta C = ctrl S = shift A = alt
type TKeyMod = 'M' | 'C' | 'S' | 'A'

/**
 * HOTKEY target
 */
export interface ITarget {
  key: string | string[]
  mod?: TKeyMod | TKeyMod[]
  desc: string
  cat?: ITargetCat
  tag?: string
  cmd: () => boolean|void
  up?: () => boolean|void
  mon?: (target:ITarget) => boolean|void
}

/**
 * KEY MODIFIER utilities
 */
export class KeyMod {
  public static M: TKeyMod = 'M'
  public static S: TKeyMod = 'S'
  public static C: TKeyMod = 'C'
  public static A: TKeyMod = 'A'
  /**
   * ENCODE modifier keys from collection as string (for comparison)
   */
  private static encode (mods?: TKeyMod[]|TKeyMod) : string {
    if (!mods) return ''
    return this
      .normalize(mods)
      .reduce((p,c) => p.concat(c),[] as string[])
      .join('')
  }

  /**
   *  NORMALIZE modifiers to array
   */
  static normalize ($mods: TKeyMod[]|TKeyMod) : TKeyMod[] {
      const mods = Array.isArray($mods) ? [...$mods] : [$mods]
      const seen = {}
      // DEDUPE modifiers
      return mods
        .filter(m => !seen[m] && (seen[m] = true))
        .sort()
  }

  /**
   * ENCODE modifier keys present in event
   */
  private static encodeEvent (e:KeyboardEvent) : string {
    return this
      .normalize([e.metaKey && this.M,e.shiftKey && this.S,e.ctrlKey && this.C,e.altKey && this.A].filter(_ => _) as TKeyMod[])
      .join('')
  }

  /**
   * IS modifier set FULLY PRESENT in event?
   * @param e 
   */
  static test (e:KeyboardEvent,mods?: TKeyMod[]|TKeyMod) : boolean {
    return KeyMod.encodeEvent(e) === KeyMod.encode(mods)
  }

  /**
   * TRANSFORM modifier to near-printable form (as collection of strings)
   * @param mods 
   */
  static toStrings (mods?: TKeyMod[]|TKeyMod) : string[] {
    // INDEX by mod type
    const index = this
      .normalize(mods||[])
      .reduce((p,c) => { p[c] = c; return p },{})
    return [index[this.M] && 'Cmd',index[this.S] && 'Shift',index[this.C] && 'Ctrl',index[this.A] && 'Alt']
      .filter(_ => _)
      .sort()
  }
}

class TargetKey {
  static normalize(key:string) : string {
    return /^[a-z]$/.test(key)
      ? key.toUpperCase()
      : key
  }
}

/**
 * HOTKEY TARGET utitlities
 */
export class Target {
  /**
   *  RETURN target instance if triggered by given event 
   */
  static test (self:ITarget,e:KeyboardEvent) : ITarget | undefined {
    const target = this.normalize(self)
    const eventKey = TargetKey.normalize(e.key)
    return (KeyMod.test(e,target.mod) && target.key.includes(eventKey))
      ? target
      : undefined
  }

  /**
   * NORMALIZE target state
   */
  static normalize (self: ITarget) : ITarget {
    const target = {...self}
    // NORMALIZE targeted key to collection
    if (!Array.isArray(target.key)) {
      target.key = [target.key]
    }
    target.key = target.key.map(TargetKey.normalize)
    // NORMALIZE mod keys
    if (target.mod) {
      target.mod = KeyMod.normalize(target.mod!)
    }
    return target
  }

  static forEvent (targets: ITarget[], e:KeyboardEvent) : ITarget[]|undefined {
    const hots = targets.filter(target => Target.test(target,e))
    return hots.length ? hots : undefined
  }
}

/**
 * MOUSE GESTURES (modifier + click) shown in the shortcuts dialog.
 * These are NOT keyboard targets — they're click-predicate handlers
 * (Events.cmd / Events.alt in module.tsx + chain.tsx), so they live here as
 * display descriptors only. Keep `mod` in sync with those handlers.
 */
export interface IGesture {
  desc: string
  mod?: TKeyMod | TKeyMod[]
}

export const GESTURES: IGesture[] = [
  { desc: 'Delete chain (header or node)', mod: KeyMod.M },
  { desc: 'Copy node to a new chain',      mod: KeyMod.A },
]
